// ============================================================
// useDockViewportLock — iOS-Keyboard-Verhalten der AssistantBar.
//
// 1:1 aus AssistantBar.js (Vanilla) verschoben: der Dock wird bei offenem
// Panel an das visualViewport «geschraubt» (mesl-e ChatGPT), Body-Scroll
// wird gesperrt und touchmove ausserhalb von Chat/Verlaufs-Liste blockiert.
// Die Funktionen sind absichtlich wörtlich übernommen; nur die Element-
// Referenzen kommen jetzt aus Refs statt aus querySelector.
// ============================================================
import { useEffect, useRef, type RefObject } from 'react';

const _isMobile = () => window.matchMedia('(max-width: 768px)').matches;

export function useDockViewportLock(
  panelOpen: boolean,
  dockRef: RefObject<HTMLDivElement | null>,
  chatRef: RefObject<HTMLDivElement | null>,
): void {
  // Der visualViewport-Listener lebt dauerhaft (wie früher) und muss den
  // AKTUELLEN Panel-Zustand sehen — darum ein Ref statt Closure-Wert.
  const openRef = useRef(panelOpen);
  openRef.current = panelOpen;

  // Funktionen genau EINMAL erzeugen (früher Closures in mountAssistantBar).
  const fns = useRef<{
    lock: () => void;
    reset: () => void;
    block: (e: TouchEvent) => void;
  } | null>(null);
  if (!fns.current) {
    const _resetDock = () => {
      const dock = dockRef.current;
      if (!dock) return;
      dock.style.height = '';
      dock.style.top = '';
      dock.style.bottom = '';
    };
    const _lockDockToViewport = () => {
      if (!_isMobile() || !window.visualViewport) return;
      const dock = dockRef.current;
      if (!dock) return;
      // faqat vaqti panel baz ast dock ra ghofl kon
      if (!openRef.current) { _resetDock(); return; }
      const vv = window.visualViewport;
      dock.style.height = vv.height + 'px';
      dock.style.top = '0px';
      dock.style.left = '0px';
      dock.style.right = '0px';
      dock.style.bottom = 'auto';
      window.scrollTo(0, 0);
      const chat = chatRef.current;
      if (chat) chat.scrollTop = chat.scrollHeight;
    };
    // jeloye scroll-e kol-e safhe ra begir; faqat chat scroll shavad
    const _blockTouchMove = (e: TouchEvent) => {
      if (!_isMobile()) return;
      if (!openRef.current) return;
      // agar lams dakhel-e chat ya list bashad, ejaze bede
      let el = e.target as HTMLElement | null;
      const dock = dockRef.current;
      while (el && el !== dock) {
        if (el === chatRef.current || (el.classList && (el.classList.contains('ab-chat') || el.classList.contains('ab-hist-list')))) return;
        el = el.parentElement;
      }
      e.preventDefault();
    };
    fns.current = { lock: _lockDockToViewport, reset: _resetDock, block: _blockTouchMove };
  }

  // Dauerhafte visualViewport-Listener (wie im alten mountAssistantBar).
  useEffect(() => {
    if (!window.visualViewport) return;
    const { lock } = fns.current!;
    window.visualViewport.addEventListener('resize', lock);
    window.visualViewport.addEventListener('scroll', lock);
    return () => {
      window.visualViewport?.removeEventListener('resize', lock);
      window.visualViewport?.removeEventListener('scroll', lock);
    };
  }, []);

  // Öffnen/Schliessen des Panels — früher direkt in openPanel()/closePanel().
  useEffect(() => {
    const { lock, reset, block } = fns.current!;
    if (!panelOpen) return;
    if (!_isMobile()) return;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    const timer = setTimeout(lock, 60);
    document.addEventListener('touchmove', block, { passive: false });
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      reset();
      document.removeEventListener('touchmove', block);
    };
  }, [panelOpen]);
}
